/**
 * cinder.fi's origins, canonical URLs and contact addresses, defined once.
 *
 * Shared the same way as appPages.js / planLimits.js: edge functions import
 * it directly, the app imports it (src/utils/siteUrl.js re-exports the site
 * bits), the MCP server and the Vercel functions in api/ import it by
 * relative path.
 *
 * Every origin allowlist (edge-function CORS, Stripe return URLs, the public
 * sign-up endpoints, the MCP server's CORS) derives from WEB_ORIGINS here, so
 * adding or retiring a domain is one change. Before this, eight hand-kept
 * copies disagreed: a POST from https://www.cinder.fi passed the api/*
 * sign-ups but failed every _shared/cors.ts preflight.
 *
 * scripts/check-domains.mjs fails on a raw https://cinder.fi /
 * app.cinder.fi / legacy-domain URL or mailto: literal elsewhere in app and
 * server code.
 */

/** The app (Vite SPA). */
export const APP_ORIGIN = 'https://app.cinder.fi';
/** The marketing + content site (Astro). trailingSlash: 'always'. */
export const SITE_ORIGIN = 'https://cinder.fi';
/** The advisor surface. */
export const ADVISOR_ORIGIN = 'https://advisor.cinder.fi';
/** The MCP server (Railway). */
export const MCP_ORIGIN = 'https://mcp.cinder.fi';

/** Every host the marketing site is served from (canonical first). */
export const SITE_ORIGINS = Object.freeze([
  SITE_ORIGIN,
  'https://www.cinder.fi',
  // Legacy marketing domains (#419) — still redirect here; keep for the overlap.
  'https://cinderfi.com',
  'https://www.cinderfi.com',
  'https://cinderfi.ca',
  'https://www.cinderfi.ca',
]);

/** Every host the app is served from (canonical first). */
export const APP_ORIGINS = Object.freeze([
  APP_ORIGIN,
  // Legacy app domain (#419).
  'https://cinderfi.app',
  'https://www.cinderfi.app',
]);

/**
 * Every production origin of ours, app first (cors.ts falls back to the
 * first entry for a disallowed origin).
 */
export const WEB_ORIGINS = Object.freeze([...APP_ORIGINS, ...SITE_ORIGINS, ADVISOR_ORIGIN]);

/**
 * Local dev servers: 5173 app (vite), 5275 advisor app, 4321/4322 Astro site,
 * 3100 MCP server, 6274 MCP Inspector.
 */
export const DEV_ORIGINS = Object.freeze([
  'http://localhost:5173',
  'http://localhost:5275',
  'http://localhost:4321',
  'http://localhost:4322',
  'http://localhost:3100',
  'http://localhost:6274',
]);

/**
 * The origins a browser endpoint accepts.
 * @param {{ dev?: boolean }} [opts] dev: also accept the local dev servers
 * @returns {string[]}
 */
export function allowedOrigins({ dev = false } = {}) {
  return dev ? [...WEB_ORIGINS, ...DEV_ORIGINS] : [...WEB_ORIGINS];
}

/** Hostnames of the marketing site (no scheme), e.g. for return-URL rewrites. */
export const SITE_HOSTS = Object.freeze(SITE_ORIGINS.map((o) => new URL(o).hostname));

/**
 * Absolute URL on the marketing site. The site is built with
 * trailingSlash: 'always', so a page path gets one ("/privacy" →
 * "/privacy/"); a file ("/llms.txt") or a path with a query/hash keeps its
 * shape apart from that.
 *
 * @param {string} [path]
 */
export function siteUrl(path = '/') {
  const raw = String(path || '/');
  const p = raw.startsWith('/') ? raw : `/${raw}`;
  const cut = p.search(/[?#]/);
  const pathname = cut === -1 ? p : p.slice(0, cut);
  const rest = cut === -1 ? '' : p.slice(cut);
  const last = pathname.split('/').pop();
  const needsSlash = !pathname.endsWith('/') && !last.includes('.');
  return `${SITE_ORIGIN}${pathname}${needsSlash ? '/' : ''}${rest}`;
}

/**
 * Absolute URL in the app. `path` may start with "/" or "#".
 * @param {string} [path]
 */
export function appUrl(path = '/') {
  const p = String(path || '/');
  if (p.startsWith('#')) return `${APP_ORIGIN}/${p}`;
  return `${APP_ORIGIN}${p.startsWith('/') ? p : `/${p}`}`;
}

/** Canonical marketing pages other code links to. */
export const SITE_URLS = Object.freeze({
  home: siteUrl('/'),
  pricing: siteUrl('/pricing/'),
  mcp: siteUrl('/mcp/'),
  docs: siteUrl('/docs/'),
  privacy: siteUrl('/privacy/'),
  terms: siteUrl('/terms/'),
  feedback: siteUrl('/feedback/'),
});

/** App deep links other code sends people to. */
export const APP_URLS = Object.freeze({
  home: `${APP_ORIGIN}/`,
  /** Claim an agent-registered MCP credential (the #claim-agent view). */
  claimAgent: appUrl('#claim-agent'),
});

/**
 * Contact addresses, one per purpose:
 *   general  — legal, privacy, and "something broke" (privacy/terms pages,
 *              the error boundary): info@
 *   support  — account and API help (MCP limits, key problems): support@
 *   feedback — product feedback and the in-app "report a problem": feedback@
 *   advisors — the advisor programme: advisors@
 */
export const CONTACT = Object.freeze({
  general: 'info@cinder.fi',
  support: 'support@cinder.fi',
  feedback: 'feedback@cinder.fi',
  advisors: 'advisors@cinder.fi',
});

/**
 * A mailto: link. Query values are URI-encoded.
 * @param {string} address
 * @param {{ subject?: string, body?: string }} [fields]
 */
export function mailto(address, { subject, body } = {}) {
  const q = [
    subject != null ? `subject=${encodeURIComponent(subject)}` : null,
    body != null ? `body=${encodeURIComponent(body)}` : null,
  ].filter(Boolean).join('&');
  return `mailto:${address}${q ? `?${q}` : ''}`;
}

/** The app's "send feedback" link (nav, feedback button, shake-to-report). */
export const FEEDBACK_MAILTO = mailto(CONTACT.feedback, { subject: 'cinder.fi feedback' });
